import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Button } from '@mui/joy';

import Arrow from '../../assets/images/Arrow.svg?react';
import LinkSvg from '../../assets/images/Link.svg?react';
import QrCodeIcon from '../../assets/images/QRCode.svg?react';
import { SharedSessionParticipant } from '../../services/types/api.generated';
import sessionStore from '../../stores/SessionStore';
import { AgentStatus } from './types';
import Collapse from './Collapse';
import PushToCommandButton from './PushToCommandButton';
import QRModal from './QRModal';

// Speaking Indicator Component with animated SVG bars
const SpeakingIndicator: React.FC = () => {
  const [heights, setHeights] = useState([0.3, 0.4, 0.5, 0.4, 0.3]);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeights([
        Math.random() * 0.5 + 0.2,
        Math.random() * 0.6 + 0.3,
        Math.random() * 0.7 + 0.4,
        Math.random() * 0.6 + 0.3,
        Math.random() * 0.5 + 0.2
      ]);
    }, 150);
    return () => clearInterval(interval);
  }, []);

  const baseHeights = [2.18, 4.14, 6.58, 4.14, 2.18]; // Base heights from SVG paths
  const baseY = [7.4354, 6.05454, 4.67368, 6.05454, 7.4354]; // Base Y positions

  return (
    <svg width='24' height='24' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      {/* Bar 1 - Right */}
      <path
        d={`M13.2895 ${8.81633 - baseHeights[0] * heights[0]
          }V7.4354C13.2895 7.0637 12.9877 6.76172 12.6162 6.76172C12.2447 6.76172 11.9429 7.0637 11.9429 7.4354V${8.81633 - baseHeights[0] * heights[0]
          }C11.9429 ${8.81633 - baseHeights[0] * heights[0] + 0.35369} 12.2447 ${8.81633 - baseHeights[0] * heights[0] + 0.65567
          } 12.6162 ${8.81633 - baseHeights[0] * heights[0] + 0.65567}C12.9884 ${8.81633 - baseHeights[0] * heights[0] + 0.65567
          } 13.2895 ${8.81633 - baseHeights[0] * heights[0] + 0.35369} 13.2895 ${8.81633 - baseHeights[0] * heights[0]}Z`}
        fill='#51BC51'
      />
      {/* Bar 2 - Left */}
      <path
        d={`M3.34667 ${8.81633 - baseHeights[1] * heights[1]
          }V7.4354C3.34667 7.0637 3.04484 6.76172 2.67333 6.76172C2.30182 6.76172 2 7.0637 2 7.4354V${8.81633 - baseHeights[1] * heights[1]
          }C2 ${8.81633 - baseHeights[1] * heights[1] + 0.35369} 2.30182 ${8.81633 - baseHeights[1] * heights[1] + 0.65567
          } 2.67333 ${8.81633 - baseHeights[1] * heights[1] + 0.65567}C3.04484 ${8.81633 - baseHeights[1] * heights[1] + 0.65567
          } 3.34667 ${8.81633 - baseHeights[1] * heights[1] + 0.35369} 3.34667 ${8.81633 - baseHeights[1] * heights[1]}Z`}
        fill='#51BC51'
      />
      {/* Bar 3 - Right middle */}
      <path
        d={`M10.8042 ${10.1974 - baseHeights[2] * heights[2]
          }V6.05454C10.8042 5.68284 10.5024 5.38086 10.1309 5.38086C9.75934 5.38086 9.45752 5.68284 9.45752 6.05454V${10.1974 - baseHeights[2] * heights[2]
          }C9.45752 ${10.1974 - baseHeights[2] * heights[2] + 0.35369} 9.75934 ${10.1974 - baseHeights[2] * heights[2] + 0.65567
          } 10.1309 ${10.1974 - baseHeights[2] * heights[2] + 0.65567}C10.5024 ${10.1974 - baseHeights[2] * heights[2] + 0.65567
          } 10.8042 ${10.1974 - baseHeights[2] * heights[2] + 0.35369} 10.8042 ${10.1974 - baseHeights[2] * heights[2]}Z`}
        fill='#51BC51'
      />
      {/* Bar 4 - Left middle */}
      <path
        d={`M5.83202 ${10.1974 - baseHeights[3] * heights[3]
          }V6.05454C5.83202 5.68284 5.5302 5.38086 5.15868 5.38086C4.78717 5.38086 4.48535 5.68284 4.48535 6.05454V${10.1974 - baseHeights[3] * heights[3]
          }C4.48535 ${10.1974 - baseHeights[3] * heights[3] + 0.35369} 4.78717 ${10.1974 - baseHeights[3] * heights[3] + 0.65567
          } 5.15868 ${10.1974 - baseHeights[3] * heights[3] + 0.65567}C5.53086 ${10.1974 - baseHeights[3] * heights[3] + 0.65567
          } 5.83202 ${10.1974 - baseHeights[3] * heights[3] + 0.35369} 5.83202 ${10.1974 - baseHeights[3] * heights[3]}Z`}
        fill='#51BC51'
      />
      {/* Bar 5 - Center */}
      <path
        d={`M8.3181 ${11.5789 - baseHeights[4] * heights[4]
          }V4.67368C8.3181 4.30198 8.01628 4 7.64477 4C7.27326 4 6.97144 4.30198 6.97144 4.67368V${11.5789 - baseHeights[4] * heights[4]
          }C6.97144 ${11.5789 - baseHeights[4] * heights[4] + 0.35369} 7.27326 ${11.5789 - baseHeights[4] * heights[4] + 0.65567
          } 7.64477 ${11.5789 - baseHeights[4] * heights[4] + 0.65567}C8.01628 ${11.5789 - baseHeights[4] * heights[4] + 0.65567
          } 8.3181 ${11.5789 - baseHeights[4] * heights[4] + 0.35369} 8.3181 ${11.5789 - baseHeights[4] * heights[4]}Z`}
        fill='#51BC51'
      />
    </svg>
  );
};

interface SettingsAccordionProps {
  subTitle?: string;
  participants: SharedSessionParticipant[]; // From ping/pong
  sessionId: string;
  participantId: string;
  deviceUuid: string;
  viewerCount: number;
  microphoneEnabled?: boolean;
  isVoiceChatConnected?: boolean;
  isVoiceChatConnecting?: boolean;
  onToggleMicrophone?: () => Promise<void>;
  onToggleVoiceChat?: () => Promise<void>;
  voiceChatParticipants?: Array<{ id: string; name: string; isLocal?: boolean }>; // From voice chat
  activeSpeakers?: Array<{ id: string }>; // Active speakers from voice chat
  // Push-to-command props
  agentStatus?: AgentStatus;
  setAgentStatus?: (status: AgentStatus) => void;
  droneUuids?: string[];
  onSetMicrophone?: (enabled: boolean) => Promise<void>;
}

const SettingsAccordion: React.FC<SettingsAccordionProps> = ({
  subTitle = '',
  participants,
  sessionId,
  participantId,
  deviceUuid,
  viewerCount,
  microphoneEnabled = false,
  isVoiceChatConnected = false,
  isVoiceChatConnecting = false,
  onToggleMicrophone,
  onToggleVoiceChat,
  voiceChatParticipants = [],
  activeSpeakers = [],
  agentStatus,
  setAgentStatus,
  droneUuids,
  onSetMicrophone
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [openCollapses, setOpenCollapses] = useState<Set<string>>(new Set(['share']));
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Merge participants: combine ping/pong participants with voice chat participants
  // If a participant exists in voice chat but not in ping/pong, add them
  const participantMap = new Map<string, SharedSessionParticipant>();

  // Add all ping/pong participants
  participants.forEach((p) => {
    if (p.id) {
      participantMap.set(p.id, p);
    }
  });

  // Add voice chat participants that aren't in ping/pong list
  voiceChatParticipants.forEach((vc) => {
    if (!participantMap.has(vc.id)) {
      // Create a SharedSessionParticipant-like object for voice chat participants
      participantMap.set(vc.id, {
        id: vc.id,
        display_name: vc.name,
        is_online: true
      } as SharedSessionParticipant);
    }
  });

  const mergedParticipants = Array.from(participantMap.values());

  // Check if a participant is speaking
  const isParticipantSpeaking = (pId: string) => {
    return activeSpeakers.some((speaker) => speaker.id === pId);
  };

  const handleToggleMicrophone = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent accordion toggle
    if (onToggleMicrophone) {
      try {
        await onToggleMicrophone();
      } catch (err) {
        console.error('Failed to toggle microphone:', err);
      }
    }
  };

  const handleToggleVoiceChat = async () => {
    if (onToggleVoiceChat) {
      try {
        await onToggleVoiceChat();
      } catch (err) {
        console.error('Failed to toggle voice chat:', err);
      }
    }
  };

  const link = `${window.location.origin}/crew/${sessionId}/device/${deviceUuid}`;

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setIsLinkCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setIsLinkCopied(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const typographyStyle = {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '150%',
    height: '21px',
    letterSpacing: '0px',
    flex: 'none',
    order: 3,
    flexGrow: 1
  };

  const buttonStyle = (activeLink: boolean) => ({
    bgcolor: 'transparent',
    borderRadius: 'var(--joy-radius-sm)',
    color: activeLink ? 'success.400' : 'neutral.100',
    transition: 'all 0.2s ease-in-out',
    '& svg': {
      fill: 'currentColor'
    },
    '&:hover': {
      bgcolor: activeLink ? 'transparent' : 'neutral.300',
      color: activeLink ? 'success.400' : 'neutral.plainHoverBg'
    },
    '&:active': {
      bgcolor: 'primary.bbYellow',
      color: 'neutral.plainHoverBg'
    }
  });

  const handleCollapseToggle = (collapseId: string) => {
    setOpenCollapses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(collapseId)) {
        newSet.delete(collapseId);
      } else {
        newSet.add(collapseId);
      }
      return newSet;
    });
  };

  return (
    <>
      <Box
        className='hidden sm:flex absolute left-4 bottom-4 z-30 flex flex-col'
        sx={{
          pointerEvents: 'auto',
          // Mobile landscape: show and position at top left, bottom aligned with dock top
          '@media (orientation: landscape) and (min-width: 910px) and (max-width: 1024px)': {
            display: 'flex',
            left: '0px',
            right: 'auto',
            bottom: 0
          },
          '@media (orientation: landscape) and (max-width: 909px)': {
            display: 'flex',
            top: '40px',
            left: '0px',
            bottom: '38px', // Align with full-width dock
            right: 'auto'
          }
        }}
      >
        {/* Accordion Content - Expands Upward */}
        {isExpanded && (
          <Box
            className='min-w-[337px] border border-transparent rounded-xl p-[9px] flex flex-col gap-2'
            sx={{
              bgcolor: 'background.level1',
              borderLeft: 0,
              borderRadius: '8px',
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
              // Custom scrollbar styling for better UX
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'neutral.600',
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: 'neutral.500'
                }
              }
            }}
          >
            <Box className='flex px-1 items-center justify-between w-full my-2'>
              <Typography
                level='body-md'
                sx={{
                  fontWeight: 600,
                  lineHeight: '142%',
                  letterSpacing: '0px',
                  color: 'common.offWhite'
                }}
              >
                {t('crew_view.settings.share')}
              </Typography>
              <Box className='cursor-pointer' onClick={() => setIsExpanded(!isExpanded)}>
                <Arrow
                  className='w-3 h-3'
                  fill='var(--joy-palette-common-offWhite)'
                  style={{ rotate: !isExpanded ? '180deg' : '0deg' }}
                />
              </Box>
            </Box>
            <Collapse
              title={t('crew_view.settings.invite')}
              collapseId='share'
              isOpen={openCollapses.has('share')}
              onToggle={() => handleCollapseToggle('share')}
            >
              <>
                <Box className='px-3 py-2' sx={{ bgcolor: 'background.level2' }}>
                  <Typography
                    level='body-md'
                    sx={{
                      ...typographyStyle,
                      color: 'text.primary',
                      minWidth: 'max-content'
                    }}
                  >
                    {subTitle}
                  </Typography>
                </Box>
                <Box className='w-full h-13 flex items-center'>
                  <Box className='flex-1 flex justify-center'>
                    <Button
                      onClick={handleCopyLink}
                      className='flex flex-col items-center gap-1 w-[calc(100%-16px)]'
                      sx={buttonStyle(isLinkCopied)}
                    >
                      <Box className='w-4 h-4'>
                        <LinkSvg />
                      </Box>
                      <Typography level='body-md' sx={{ color: 'inherit', fontSize: '0.75rem' }}>
                        {isLinkCopied ? t('crew_view.settings.link_copied') : t('crew_view.settings.copy_link')}
                      </Typography>
                    </Button>
                  </Box>
                  <Box
                    className='h-[44px]'
                    sx={{
                      borderLeft: 1,
                      borderColor: 'neutral.200'
                    }}
                  />
                  <Box className='flex-1 flex justify-center'>
                    <Button
                      onClick={() => setIsQrModalOpen(true)}
                      className='flex flex-col items-center gap-1 w-[calc(100%-16px)]'
                      sx={buttonStyle(false)}
                    >
                      <Box className='w-4 h-4'>
                        <QrCodeIcon />
                      </Box>
                      <Typography level='body-md' sx={{ color: 'inherit', fontSize: '0.75rem' }}>
                        {t('crew_view.settings.show_qr_code')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </>
            </Collapse>

            <Collapse
              title={t('crew_view.settings.currently_viewing', { count: viewerCount })}
              collapseId='viewing'
              isOpen={openCollapses.has('viewing')}
              onToggle={() => handleCollapseToggle('viewing')}
            >
              <Box
                className='flex flex-col gap-2 py-2 px-3'
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'neutral.600',
                    borderRadius: '2px',
                    '&:hover': {
                      backgroundColor: 'neutral.500'
                    }
                  }
                }}
              >
                {mergedParticipants.map((participant) => {
                  const isSpeaking = participant.id ? isParticipantSpeaking(participant.id) : false;
                  return (
                    <Box
                      className='flex flex-row items-center gap-2'
                      key={participant.id}
                      sx={{
                        p: 1,
                        borderRadius: 'sm',
                        bgcolor: isSpeaking ? 'success.900' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <Box className='flex items-center gap-2 flex-1'>
                        {isSpeaking && (
                          <Box className='flex justify-center gap-1'>
                            {[
                              { h: 20, dur: 420, delay: 0 },
                              { h: 26, dur: 520, delay: 60 },
                              { h: 32, dur: 360, delay: 30 }
                            ].map((b, i) => (
                              <Box
                                key={i}
                                className='w-[2px] rounded-sm opacity-70 transition-[height] duration-100 animate-bounceY'
                                sx={{ bgcolor: 'success.400' }}
                                style={{
                                  height: '10px',
                                  animationDuration: `${b.dur}ms`,
                                  animationDelay: `${b.delay}ms`
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography
                          level='body-lg'
                          sx={{
                            ...typographyStyle,
                            color: isSpeaking ? 'success.400' : 'text.primary',
                            minWidth: 'max-content',
                            fontWeight: isSpeaking ? 600 : 400
                          }}
                        >
                          {participant.display_name || t('crew_view.settings.you')}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        )}

        {!isExpanded && (
          <Box
            className='absolute left-0 bottom-0 z-40 cursor-pointer rounded-lg p-2'
            sx={{
              bgcolor: 'background.level1'
            }}
          >
            <Box className='flex items-center justify-between w-full pb-2'>
              <Typography
                level='body-sm'
                sx={{
                  fontWeight: 600,
                  lineHeight: '142%',
                  letterSpacing: '0px',
                  color: 'common.offWhite'
                }}
              >
                {t('crew_view.settings.share')}
              </Typography>
              <Box className='cursor-pointer' onClick={() => setIsExpanded(!isExpanded)}>
                <Arrow className='w-3 h-3 rotate-[180deg]' fill='var(--joy-palette-common-offWhite)' />
              </Box>
            </Box>
            <Box className='flex gap-2'>
              {/* Left Column - Microphone Control */}
              <Box
                className='flex flex-col items-center justify-center gap-2 w-14'
                sx={{
                  border: 1,
                  borderColor: 'primary.bbYellow',
                  borderRadius: 'sm',
                  minWidth: 56,
                  minHeight: 79,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  bgcolor: 'background.level1'
                }}
              >
                {false && microphoneEnabled && (
                  <Box
                    className='w-6 h-3.5 flex items-center justify-center'
                    sx={{
                      bgcolor: '#ED1C24',
                      borderRadius: 'xs'
                    }}
                  >
                    <Typography
                      level='body-xs'
                      sx={{
                        fontWeight: 700,
                        color: 'primary.offWhite',
                        textTransform: 'uppercase'
                      }}
                    >
                      {t('crew_view.settings.rec')}
                    </Typography>
                  </Box>
                )}

                {/* Switch - Plain HTML Implementation */}
                <Box
                  className='cursor-pointer'
                  component='label'
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    width: 40,
                    height: 20,
                    opacity: !isVoiceChatConnected || !onToggleMicrophone ? 0.5 : 1
                  }}
                >
                  <input
                    type='checkbox'
                    checked={microphoneEnabled}
                    onChange={handleToggleMicrophone}
                    disabled={!isVoiceChatConnected || !onToggleMicrophone}
                    style={{
                      opacity: 0,
                      width: 0,
                      height: 0,
                      position: 'absolute'
                    }}
                  />
                  {/* Track */}
                  <Box
                    className='absolute top-0 left-0 right-0 bottom-0'
                    sx={{
                      bgcolor: microphoneEnabled ? 'primary.focusVisible' : 'neutral.800',
                      border: 1,
                      borderColor: microphoneEnabled ? 'primary.focusVisible' : 'neutral.200',
                      borderRadius: '16px'
                    }}
                  />
                  {/* Thumb */}
                  <Box
                    className='transition-all duration-200 ease-in-out'
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: microphoneEnabled ? 'calc(50% + 5px)' : '4px',
                      width: 12,
                      height: 12,
                      transform: 'translateY(-50%)',
                      bgcolor: microphoneEnabled ? 'neutral.600' : 'neutral.200',
                      border: 1,
                      borderColor: microphoneEnabled ? 'neutral.600' : 'neutral.200',
                      borderRadius: '50%'
                    }}
                  />
                </Box>

                {/* Mic Label */}
                <Typography
                  level='body-sm'
                  sx={{
                    fontWeight: 400,
                    color: 'common.offWhite',
                    fontSize: '0.75rem',
                    lineHeight: '142%'
                  }}
                >
                  {t('crew_view.settings.mic')}
                </Typography>
              </Box>

              {/* Right Column - Participants */}
              <Box
                sx={{
                  bgcolor: 'background.level1',
                  border: 1,
                  borderColor: 'primary.bbYellow',
                  borderRadius: 'md',
                  px: 1.5,
                  py: 1,
                  minWidth: 166,
                  minHeight: 86,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                {mergedParticipants.map((participant) => {
                  const isSpeaking = participant.id ? isParticipantSpeaking(participant.id) : false;
                  return (
                    <Box
                      key={`participant-header-${participant.id}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      {isSpeaking && <SpeakingIndicator />}
                      <Typography
                        level='body-sm'
                        sx={{
                          fontWeight: isSpeaking ? 700 : 600,
                          lineHeight: '142%',
                          letterSpacing: '0px',
                          color: isSpeaking ? 'success.400' : 'common.offWhite',
                          minWidth: 'max-content'
                        }}
                      >
                        {participant.display_name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Push-to-Command Button */}
            {sessionStore.isAuthenticated && agentStatus && setAgentStatus && droneUuids && (
              <Box sx={{ mt: 1 }}>
                <PushToCommandButton
                  sessionId={sessionId}
                  droneUuids={droneUuids}
                  agentStatus={agentStatus}
                  setAgentStatus={setAgentStatus}
                  onSetMicrophone={onSetMicrophone}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <QRModal linkToShare={link} isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)} />
    </>
  );
};

export default SettingsAccordion;
